import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useAudioAmplitude — produces a live 0..1 amplitude value for driving
 * HavnTalkWave's animation during the SPEAKING state.
 *
 * Handles two audio sources:
 *
 *   1. ElevenLabs: an HTMLAudioElement playing a blob/URL.
 *      Hooks a Web Audio AnalyserNode to the element via
 *      createMediaElementSource and reads time-domain data at 60fps.
 *      Produces real articulation-tracking amplitude.
 *
 *   2. speechSynthesis fallback: when the browser's built-in TTS is
 *      playing, there is NO audio element to analyze — the synth bypasses
 *      Web Audio entirely. Instead we drive a synthetic amplitude envelope
 *      from SpeechSynthesisUtterance boundary events, which fire per word.
 *      Each word pulses amplitude 0.2 → 0.7 → 0.2 over ~220ms.
 *
 * This second path matters RIGHT NOW because ElevenLabs quota is exhausted
 * until April 20, so until then every session falls back to speechSynthesis.
 * Without this fallback path the wave would sit frozen during every
 * assistant reply.
 *
 * Usage in HomePage.jsx:
 *
 *   const { amplitude, attachAudioElement, attachSpeechUtterance, detach } =
 *     useAudioAmplitude();
 *
 *   // When ElevenLabs audio blob arrives and starts playing:
 *   attachAudioElement(audioRef.current);
 *
 *   // When falling back to speechSynthesis:
 *   const utterance = new SpeechSynthesisUtterance(text);
 *   attachSpeechUtterance(utterance);
 *   speechSynthesis.speak(utterance);
 *
 *   // When playback ends OR user interrupts:
 *   detach();
 *
 *   // Pass amplitude into the wave:
 *   <HavnTalkWave state={waveState} amplitude={amplitude} />
 */
export default function useAudioAmplitude() {
  const [amplitude, setAmplitude] = useState(0);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const dataArrayRef = useRef(null);
  const frameRef = useRef(0);

  const attachedElementRef = useRef(null);
  const currentUtteranceRef = useRef(null);

  const syntheticAmplitudeRef = useRef(0);
  const syntheticDecayRafRef = useRef(0);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      audioContextRef.current = new AC();
    }
    if (audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume().catch(() => {});
    }
    return audioContextRef.current;
  }, []);

  const cleanupWebAudio = useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    }
    if (analyserRef.current) {
      try { analyserRef.current.disconnect(); } catch {}
      analyserRef.current = null;
    }
    dataArrayRef.current = null;
    attachedElementRef.current = null;
  }, []);

  const cleanupSpeech = useCallback(() => {
    if (syntheticDecayRafRef.current) {
      cancelAnimationFrame(syntheticDecayRafRef.current);
      syntheticDecayRafRef.current = 0;
    }
    currentUtteranceRef.current = null;
    syntheticAmplitudeRef.current = 0;
  }, []);

  const startWebAudioLoop = useCallback(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    const loop = () => {
      const analyser = analyserRef.current;
      const dataArray = dataArrayRef.current;
      if (!analyser || !dataArray) return;

      analyser.getByteTimeDomainData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      const mapped = Math.min(1, Math.pow(rms * 2.2, 0.85));

      setAmplitude(mapped);
      frameRef.current = requestAnimationFrame(loop);
    };
    frameRef.current = requestAnimationFrame(loop);
  }, []);

  const attachAudioElement = useCallback((audioEl) => {
    if (!audioEl) return;
    const ctx = getAudioContext();
    if (!ctx) {
      console.warn("[useAudioAmplitude] Web Audio API unavailable");
      return;
    }

    cleanupWebAudio();

    try {
      let sourceNode = audioEl._havnSourceNode;
      if (!sourceNode) {
        sourceNode = ctx.createMediaElementSource(audioEl);
        audioEl._havnSourceNode = sourceNode;
      }

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.4;

      sourceNode.connect(analyser);
      analyser.connect(ctx.destination);

      analyserRef.current = analyser;
      sourceNodeRef.current = sourceNode;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
      attachedElementRef.current = audioEl;

      startWebAudioLoop();
    } catch (err) {
      console.warn("[useAudioAmplitude] Failed to attach audio element:", err);
    }
  }, [getAudioContext, cleanupWebAudio, startWebAudioLoop]);

  const attachSpeechUtterance = useCallback((utterance) => {
    if (!utterance) return;

    cleanupSpeech();
    cleanupWebAudio();

    currentUtteranceRef.current = utterance;

    utterance.addEventListener("boundary", () => {
      syntheticAmplitudeRef.current = 0.7;
    });

    utterance.addEventListener("end", () => {
      syntheticAmplitudeRef.current = 0;
      setAmplitude(0);
    });
    utterance.addEventListener("error", () => {
      syntheticAmplitudeRef.current = 0;
      setAmplitude(0);
    });

    if (syntheticDecayRafRef.current) cancelAnimationFrame(syntheticDecayRafRef.current);
    const decayLoop = () => {
      const target = currentUtteranceRef.current && window.speechSynthesis.speaking ? 0.2 : 0;
      syntheticAmplitudeRef.current += (target - syntheticAmplitudeRef.current) * 0.08;
      setAmplitude(Math.max(0, Math.min(1, syntheticAmplitudeRef.current)));
      syntheticDecayRafRef.current = requestAnimationFrame(decayLoop);
    };
    syntheticDecayRafRef.current = requestAnimationFrame(decayLoop);
  }, [cleanupSpeech, cleanupWebAudio]);

  const detach = useCallback(() => {
    cleanupWebAudio();
    cleanupSpeech();
    setAmplitude(0);
  }, [cleanupWebAudio, cleanupSpeech]);

  useEffect(() => {
    return () => {
      cleanupWebAudio();
      cleanupSpeech();
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, [cleanupWebAudio, cleanupSpeech]);

  return {
    amplitude,
    attachAudioElement,
    attachSpeechUtterance,
    detach,
  };
}
